import React, { useState } from "react"

import { ChevronDown, ChevronRight } from "lucide-react"

import CostBreakdownCard, { type CostLine } from "@/components/shared/CostBreakdownCard"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { type WeekdayDataPoint } from "@/lib/weekdayComparison"
import type { RouteCostBreakdown } from "@/types/collections"

import { getLoadFactorColor } from "./loadFactorColors"

interface DailyBreakdownRowProps {
  point: WeekdayDataPoint
  usedDisplay: number
}

const DailyBreakdownRow: React.FC<DailyBreakdownRowProps> = ({
  point,
  usedDisplay,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const excludedCount = point.excludedRoutes.length

  return (
    <>
      <tr className="border-b hover:bg-palette-jungleGreen000">
        <td className="py-2 px-4">{point.dateLabel}</td>
        <td
          className={cn(
            "py-2 px-4 text-right font-medium",
            getLoadFactorColor(point.loadFactorForecast),
          )}
        >
          {point.loadFactorForecast.toFixed(1)}%
        </td>
        <td
          className={cn(
            "py-2 px-4 text-right font-medium",
            point.loadFactorActual > 0
              ? getLoadFactorColor(point.loadFactorActual)
              : "text-palette-jungleGreen400",
          )}
        >
          {point.loadFactorActual > 0 ? `${point.loadFactorActual.toFixed(1)}%` : "—"}
        </td>
        <td className="py-2 px-4 text-right">
          <span className="text-green-700 font-medium">{point.routesIncluded}</span>
          {point.routesExcluded > 0 && (
            <span className="text-palette-jungleGreen400 text-xs ml-1">
              ({point.routesExcluded} excl.)
            </span>
          )}
        </td>
        <td className="py-2 px-4 text-right">{usedDisplay.toFixed(1)}</td>
        <td className="py-2 px-4 text-right">{point.totalCapacity.toFixed(1)}</td>
        <td className="py-2 px-4 text-center">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-center gap-1 text-palette-jungleGreen600 hover:text-palette-jungleGreen900 transition-colors"
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="text-xs">View</span>
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b bg-palette-jungleGreen000">
          <td colSpan={7} className="py-4 px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {point.routeDetails.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-green-700 mb-3">
                    Included Routes ({point.routeDetails.length})
                  </div>
                  <div className="space-y-4">
                    {point.routeDetails.map((route) => (
                      <div
                        key={route.id}
                        className="border border-palette-jungleGreen200 rounded-lg p-3 bg-white"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-palette-jungleGreen900">
                              {route.code}
                            </span>
                            {route.isAdHoc && (
                              <Badge variant="secondary" className="text-[10px] font-normal">
                                Ad-hoc
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span
                              className={cn(
                                "font-medium",
                                getLoadFactorColor(route.loadFactorForecast),
                              )}
                            >
                              Forecast: {route.loadFactorForecast.toFixed(1)}%
                            </span>
                            {route.loadFactorActual > 0 && (
                              <span
                                className={cn(
                                  "font-medium",
                                  getLoadFactorColor(route.loadFactorActual),
                                )}
                              >
                                Actual: {route.loadFactorActual.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-palette-jungleGreen500 mb-2">
                          {route.usedForecast.toFixed(1)} / {route.capacity.toFixed(1)} UK pallets
                          {route.usedActual > 0 && (
                            <span className="ml-2">(Actual: {route.usedActual.toFixed(1)})</span>
                          )}
                        </div>
                        {route.stops.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-palette-jungleGreen100">
                            <div className="text-xs font-medium text-palette-jungleGreen600 mb-1">
                              Stops:
                            </div>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {route.stops.map((stop, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between text-xs py-1"
                                >
                                  <span className="text-palette-jungleGreen700">
                                    {stop.sequence}. {stop.name}
                                  </span>
                                  <div className="flex items-center gap-2 text-palette-jungleGreen600">
                                    <span>
                                      Forecast: {stop.loadingUnitsForecastUKPallets.toFixed(2)}{" "}
                                      Pallets (UK)
                                    </span>
                                    {stop.loadingUnitsActualUKPallets !== undefined &&
                                      stop.loadingUnitsActualUKPallets !== null &&
                                      stop.loadingUnitsActualUKPallets > 0 && (
                                        <span className="text-blue-600">
                                          Actual: {stop.loadingUnitsActualUKPallets.toFixed(2)}{" "}
                                          Pallets (UK)
                                        </span>
                                      )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {point.excludedRoutes.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-palette-jungleGreen600 mb-3">
                    Excluded Routes ({point.excludedRoutes.length})
                  </div>
                  <div className="space-y-2 mb-3">
                    {point.excludedRoutes.map((route) => (
                      <div
                        key={route.id}
                        className="border border-palette-jungleGreen200 rounded-lg p-3 bg-palette-jungleGreen000"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-palette-jungleGreen700">
                            {route.code}
                          </div>
                          <Badge variant="outline" className="text-xs text-palette-jungleGreen500">
                            {route.reason}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>

                  {point.excludedRoutesTotalCost && (
                    <div className="mt-3 pt-3 border-t border-palette-jungleGreen200">
                      <CostBreakdownCard
                        density="compact"
                        title="Total Cost for Excluded Routes"
                        subtitle={`${excludedCount} route${excludedCount !== 1 ? "s" : ""}`}
                        lines={excludedCostLines(point.excludedRoutesTotalCost)}
                        total={point.excludedRoutesTotalCost.total_cost}
                        perParcel={{
                          cost: point.excludedRoutesTotalCost.cost_per_parcel,
                          parcels: point.excludedRoutesTotalCost.total_forecast_volume,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {point.routeDetails.length === 0 && point.excludedRoutes.length === 0 && (
                <div className="text-sm text-palette-jungleGreen500">
                  No routes found for this date
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/** The excluded routes' summed lines; charging and wage only when they cost anything. */
function excludedCostLines(cost: RouteCostBreakdown): CostLine[] {
  const lines: CostLine[] = [
    { kind: "lease", label: "Vehicle Lease", value: cost.vehicle_lease_cost },
  ]
  if (cost.charging_cost > 0) {
    lines.push({ kind: "charging", label: "Charging Cost", value: cost.charging_cost })
  }
  if (cost.driver_wage_cost > 0) {
    lines.push({ kind: "wage", label: "Driver Wage", value: cost.driver_wage_cost })
  }
  return lines
}

export default DailyBreakdownRow
